import React, { useState, useMemo } from 'react';
import { Calculator, Users, Music, Utensils, Building2 } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';

export function ClassReunionEstimator() {
  const [attendees, setAttendees] = useState<number>(250);
  const [venueCost, setVenueCost] = useState<number>(3500);
  const [cateringPerHead, setCateringPerHead] = useState<number>(45);
  const [entertainmentCost, setEntertainmentCost] = useState<number>(1200);
  const [bufferPercent, setBufferPercent] = useState<number>(15);

  const model = useMemo(() => {
    const fixedCosts = venueCost + entertainmentCost;
    const variableCosts = cateringPerHead * attendees;
    const subtotal = fixedCosts + variableCosts;
    const bufferAmount = subtotal * (bufferPercent / 100);
    const totalRequired = subtotal + bufferAmount;

    const breakevenPerHead = attendees > 0 ? totalRequired / attendees : 0;
    
    // Tiered strategy
    // We assume an ideal distribution: 60% standard, 30% early bird, 10% door
    // But we need the weighted average to equal breakevenPerHead
    // Let S = Standard Price
    // Early = 0.85 * S
    // Door = 1.25 * S
    // 0.6S + 0.3(0.85S) + 0.1(1.25S) = breakeven
    // S(0.6 + 0.255 + 0.125) = breakeven
    // S(0.98) = breakeven => S = breakeven / 0.98

    const standardPrice = breakevenPerHead / 0.98;
    const earlyBirdPrice = standardPrice * 0.85;
    const doorPrice = standardPrice * 1.25;

    return {
      fixedCosts,
      variableCosts,
      totalRequired,
      breakevenPerHead,
      tiers: {
        earlyBird: earlyBirdPrice,
        standard: standardPrice,
        door: doorPrice
      }
    };
  }, [attendees, venueCost, cateringPerHead, entertainmentCost, bufferPercent]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Calculator className="w-6 h-6 mr-3 text-emerald-200" />
            Class Reunion Budget Estimator
          </h2>
          <p className="text-emerald-100 max-w-xl">
            Ensure your reunion covers its costs. Calculate break-even ticket prices and build tiered pricing structures to fund venues, catering, and entertainment.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        
        {/* Inputs */}
        <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
                <Users className="w-4 h-4 mr-2 text-teal-500" /> Expected Attendees
              </label>
              <Input 
                type="number" 
                value={attendees} 
                onChange={(e) => setAttendees(Number(e.target.value))}
                className="text-lg font-bold"
              />
            </div>
            
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
                <Building2 className="w-4 h-4 mr-2 text-teal-500" /> Venue Cost ($)
              </label>
              <Input 
                type="number" 
                value={venueCost} 
                onChange={(e) => setVenueCost(Number(e.target.value))}
              />
            </div>
            
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
                <Music className="w-4 h-4 mr-2 text-teal-500" /> Entertainment ($)
              </label>
              <Input 
                type="number" 
                value={entertainmentCost} 
                onChange={(e) => setEntertainmentCost(Number(e.target.value))}
              />
            </div>

            <div className="col-span-2">
              <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
                <Utensils className="w-4 h-4 mr-2 text-teal-500" /> Catering Cost Per Head ($)
              </label>
              <Input 
                type="number" 
                value={cateringPerHead} 
                onChange={(e) => setCateringPerHead(Number(e.target.value))}
                className="text-lg"
              />
            </div>

            <div className="col-span-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-sm font-semibold text-slate-500 uppercase mb-2 block">
                Safety Buffer: <span className="text-slate-900 dark:text-white font-bold">{bufferPercent}%</span>
              </label>
              <input 
                type="range" 
                min="0" 
                max="50" 
                value={bufferPercent} 
                onChange={(e) => setBufferPercent(Number(e.target.value))}
                className="w-full accent-teal-600"
              />
              <p className="text-xs text-slate-400 mt-2">Buffer protects against lower-than-expected attendance.</p>
            </div>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-slate-900 text-white">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Total Funding Required</h3>
            <div className="text-5xl font-black text-emerald-400 tracking-tight">
              {formatCurrency(model.totalRequired)}
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-slate-800">
              <div>
                <div className="text-sm text-slate-400">Fixed Costs</div>
                <div className="text-xl font-bold">{formatCurrency(model.fixedCosts)}</div>
              </div>
              <div>
                <div className="text-sm text-slate-400">Variable Costs</div>
                <div className="text-xl font-bold">{formatCurrency(model.variableCosts)}</div>
              </div>
            </div>
          </Card>

          <Card className="p-0 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 overflow-hidden">
            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
              <h3 className="font-bold">Suggested Ticket Tiers</h3>
              <div className="text-sm text-slate-500">Target Breakeven: <span className="font-bold text-slate-900 dark:text-white">{formatCurrency(model.breakevenPerHead)}</span></div>
            </div>
            
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              <div className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <div>
                  <div className="font-bold text-teal-600 dark:text-teal-400">Early Bird Ticket</div>
                  <div className="text-sm text-slate-500">Assumes ~30% of sales</div>
                </div>
                <div className="text-2xl font-black">
                  {formatCurrency(model.tiers.earlyBird)}
                </div>
              </div>
              
              <div className="p-4 flex items-center justify-between bg-teal-50 dark:bg-teal-900/10">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Standard Ticket</div>
                  <div className="text-sm text-slate-500">Assumes ~60% of sales</div>
                </div>
                <div className="text-2xl font-black text-teal-700 dark:text-teal-300">
                  {formatCurrency(model.tiers.standard)}
                </div>
              </div>
              
              <div className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <div>
                  <div className="font-bold text-orange-600 dark:text-orange-400">At-The-Door Premium</div>
                  <div className="text-sm text-slate-500">Assumes ~10% of sales</div>
                </div>
                <div className="text-2xl font-black">
                  {formatCurrency(model.tiers.door)}
                </div>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
